import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import type {
  IntegrityResponse,
  ItemAnalysisResponse,
  OverviewResponse,
  PeopleResponse,
  TestsResponse,
} from 'modules/analytics';
import type { AnalyticsFilters, AnalyticsTab, DatePreset } from 'pages/Analytics/useAnalyticsFilters';
import { PERIOD_LABEL_KEY, countPendingAnswers, type PendingTestGroup } from 'pages/Analytics/analyticsFormat';
import KpiRow from 'pages/Analytics/components/KpiRow';
import { NeedsAttentionQueue } from 'pages/Analytics/components/NeedsAttentionQueue';
import { SubmissionsBarChart } from 'pages/Analytics/components/SubmissionsBarChart';
import { Button } from 'components/ui/button';
import { BottomSheet, ListCard, M, MobileEmpty, MobileSkeleton, ScreenHeader, ScrollTabs } from 'components/mobile';
import { IntegrityTab, PeopleTab, RetryPanel, TestsTab, type Query } from './MobileAnalyticsTabs';

type Props = {
  filters: AnalyticsFilters;
  hasAdvanced: boolean;
  effectiveTab: AnalyticsTab;
  overview: Query<OverviewResponse>;
  people: Query<PeopleResponse>;
  tests: Query<TestsResponse>;
  items: Query<ItemAnalysisResponse>;
  integrity: Query<IntegrityResponse>;
  /** Pending manual grading, per test. */
  pendingGroups: PendingTestGroup[];
  pendingLoading: boolean;
};

const PERIODS: DatePreset[] = ['today', '7d', '30d', 'term'];

function hasFilters(filters: AnalyticsFilters) {
  return filters.preset !== '30d' || Boolean(filters.testId || filters.teamId || filters.q);
}

/** The period choice, as the phone form of the desktop period control. Applies at once. */
function FilterSheet({ filters, onClose }: { filters: AnalyticsFilters; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-[17px] font-bold" style={{ color: M.ink }}>
          {t('filters')}
        </h2>
        {hasFilters(filters) && (
          <button
            type="button"
            onClick={() => {
              filters.setPreset('30d');
              filters.setTestId(undefined);
              filters.setTeamId(undefined);
              filters.setQ('');
            }}
            className="h-9 text-[14px] font-medium hover:underline"
            style={{ color: M.ink2 }}
          >
            {t('clear')}
          </button>
        )}
      </div>
      <p className="mt-1 text-[13px]" style={{ color: M.ink3 }}>
        {t('analytics_filter_hint')}
      </p>
      <section className="mt-5">
        <h3 className="mb-2 text-[13px] font-semibold" style={{ color: M.ink2 }}>
          {t('analytics_period')}
        </h3>
        <div className="flex flex-wrap gap-2">
          {PERIODS.map((period) => {
            const active = filters.preset === period;
            return (
              <button
                key={period}
                type="button"
                aria-pressed={active}
                onClick={() => filters.setPreset(period)}
                className="inline-flex h-11 items-center rounded-full px-4 text-[14px] font-medium"
                style={
                  active
                    ? { background: M.ink, color: '#FFFFFF', border: `1px solid ${M.ink}` }
                    : { background: M.card, color: M.ink2, border: `1px solid ${M.border}` }
                }
              >
                {t(PERIOD_LABEL_KEY[period])}
              </button>
            );
          })}
        </div>
      </section>
      <button
        type="button"
        onClick={onClose}
        className="mt-6 h-12 w-full rounded-md text-[15px] font-semibold"
        style={{ background: M.primary, color: M.primaryInk }}
      >
        {t('done')}
      </button>
    </div>
  );
}

function OverviewTab({
  overview,
  pendingGroups,
  pendingLoading,
  tests,
  integrity,
  filters,
}: Pick<Props, 'overview' | 'pendingGroups' | 'pendingLoading' | 'tests' | 'integrity' | 'filters'>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = overview.data;
  const pendingAnswers = countPendingAnswers(pendingGroups);

  if (overview.isLoading) return <MobileSkeleton rows={5} />;
  if (overview.isError) {
    return <RetryPanel scope="overview" error={overview.error} onRetry={() => void overview.refetch()} />;
  }
  if (!data) return null;
  if (data.totalAttempts === 0 && pendingAnswers === 0 && hasFilters(filters)) {
    return (
      <MobileEmpty
        title={t('analytics_no_filtered_results')}
        description={t('analytics_no_filtered_results_hint')}
      />
    );
  }

  const integrityEvents = integrity.data?.byType.reduce((total, event) => total + event.count, 0) ?? 0;
  const reviewIntegrity = () => filters.setTab('integrity');

  return (
    <div className="space-y-3 px-4 pb-2 pt-3">
      {pendingAnswers > 0 && (
        <ListCard>
          <div data-mobile-grading-callout className="flex items-center gap-3 px-3.5 py-3">
            <span aria-hidden className="h-2 w-2 flex-shrink-0 rounded-full bg-warning-500" />
            <span className="min-w-0 flex-1 text-[14px] font-medium" style={{ color: M.ink }}>
              {t('na_grading', { count: pendingAnswers })}
            </span>
            {/* 32px like the board; the hit area grows to 44px around it. */}
            <Button
              size="xs"
              className="relative flex-shrink-0 after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-['']"
              onClick={() => navigate('/app/grading')}
            >
              {t('grade')}
            </Button>
          </div>
        </ListCard>
      )}

      <KpiRow
        compact
        data={data}
        isLoading={false}
        pendingAnswers={pendingAnswers}
        integrityEvents={null}
        funnel={tests.data?.funnel}
        onReviewIntegrity={reviewIntegrity}
      />

      <NeedsAttentionQueue
        pendingGroups={pendingGroups}
        integrityEvents={integrityEvents}
        onReviewIntegrity={reviewIntegrity}
        isLoading={pendingLoading}
      />

      <SubmissionsBarChart
        activity={data.recentActivity}
        isLoading={false}
        periodLabel={t(PERIOD_LABEL_KEY[filters.preset])}
        chartHeight={96}
      />
    </div>
  );
}

export function MobileAnalyticsWorkspace({
  filters,
  hasAdvanced,
  effectiveTab,
  overview,
  people,
  tests,
  items,
  integrity,
  pendingGroups,
  pendingLoading,
}: Props) {
  const { t } = useTranslation();
  const [filterOpen, setFilterOpen] = useState(false);
  const tabs = useMemo(
    () => [
      { value: 'overview', label: t('analytics_tab_overview') },
      ...(hasAdvanced
        ? [
            { value: 'people', label: t('analytics_tab_people') },
            { value: 'tests', label: t('analytics_tab_tests') },
            { value: 'integrity', label: t('analytics_tab_integrity') },
          ]
        : []),
    ],
    [hasAdvanced, t],
  );

  return (
    <>
      <ScreenHeader
        title={
          <>
            <span className="block text-[12px] font-medium leading-4" style={{ color: M.ink3 }}>
              {`${t('nav.analytics')} · ${t(PERIOD_LABEL_KEY[filters.preset])}`}
            </span>
            <span className="block">{t('analytics_pulse')}</span>
          </>
        }
        right={
          // 36px ghost icon; the tap area grows to 44px without moving the layout.
          <button
            type="button"
            aria-label={t('filters')}
            aria-expanded={filterOpen}
            onClick={() => setFilterOpen(true)}
            className="-m-1 flex h-11 w-11 items-center justify-center rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{ color: M.ink2 }}
          >
            <SlidersHorizontal size={19} aria-hidden />
          </button>
        }
      >
        <ScrollTabs
          tabs={tabs}
          value={effectiveTab}
          onChange={(value) => filters.setTab(value as AnalyticsTab)}
          aria-label={t('nav.analytics')}
          className="pt-1"
        />
      </ScreenHeader>

      {effectiveTab === 'overview' && (
        <OverviewTab
          overview={overview}
          pendingGroups={pendingGroups}
          pendingLoading={pendingLoading}
          tests={tests}
          integrity={integrity}
          filters={filters}
        />
      )}
      {effectiveTab === 'people' && <PeopleTab people={people} filters={filters} />}
      {effectiveTab === 'tests' && <TestsTab tests={tests} items={items} filters={filters} />}
      {effectiveTab === 'integrity' && <IntegrityTab integrity={integrity} />}

      <BottomSheet open={filterOpen} onClose={() => setFilterOpen(false)}>
        <FilterSheet filters={filters} onClose={() => setFilterOpen(false)} />
      </BottomSheet>
    </>
  );
}
